import { useEffect, useState } from "react";
import { Loader2, Eye, EyeOff, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, toastErr } from "@/lib/api";
import { Card, Label, inputCls, PrimaryButton } from "./ui";

export const ModelTab = () => {
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [models, setModels] = useState([]);
  const [embModels, setEmbModels] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");

  const loadModels = () => {
    api.get("/admin/models").then((r) => setModels(r.data)).catch(toastErr);
    api.get("/admin/embedding-models").then((r) => setEmbModels(r.data)).catch(toastErr);
  };
  useEffect(() => { api.get("/admin/config").then((r) => setCfg(r.data)).catch(toastErr); loadModels(); }, []);
  if (!cfg) return <Loader2 className="animate-spin text-slate-400" />;

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put("/admin/config", { model: cfg.model, embedding_model: cfg.embedding_model, ...(key ? { openrouter_key: key } : {}) });
      setCfg(r.data); setKey("");
      toast.success("Model settings saved");
      loadModels();
    } catch (e) { toastErr(e); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try {
      const r = await api.post("/admin/test");
      toast.success(`Connected: ${r.data.model} replied "${r.data.reply}" in ${r.data.latency_ms}ms`);
    } catch (e) { toastErr(e); } finally { setBusy(""); }
  };

  const shown = models.filter((m) => (m.name + m.id).toLowerCase().includes(filter.toLowerCase())).slice(0, 300);
  return (
    <div className="max-w-2xl space-y-6">
      <Card title="OpenRouter">
        <Label>API key</Label>
        <div className="relative">
          <input data-testid="openrouter-api-key-input" type={show ? "text" : "password"} className={inputCls + " pr-10"} placeholder={cfg.has_key ? `Saved: ${cfg.openrouter_key} (paste to replace)` : "sk-or-v1-…"} value={key} onChange={(e) => setKey(e.target.value)} />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
        </div>
        <p className="mt-2 text-xs text-slate-500">Get a key at openrouter.ai/keys. Stored in the database, never sent to the browser.</p>
        <div className="mt-5"><Label>Model</Label>
          <input data-testid="model-filter-input" className={inputCls + " mb-2"} placeholder="Filter models…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <select data-testid="model-select-dropdown" className={inputCls} value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })}>
            <option value="">— select a model —</option>
            {cfg.model && !shown.some((m) => m.id === cfg.model) && <option value={cfg.model}>{cfg.model}</option>}
            {shown.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.id})</option>)}
          </select>
          <p className="mt-2 text-xs text-slate-500">{models.length} models available{filter && `, ${shown.length} match`}</p>
        </div>
        <div className="mt-5"><Label>Embedding model (for knowledge search)</Label>
          <select data-testid="embedding-model-select" className={inputCls} value={cfg.embedding_model || ""} onChange={(e) => setCfg({ ...cfg, embedding_model: e.target.value })}>
            {cfg.embedding_model && !embModels.some((m) => m.id === cfg.embedding_model) && <option value={cfg.embedding_model}>{cfg.embedding_model}</option>}
            {embModels.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.id})</option>)}
          </select>
          <p className="mt-2 text-xs text-slate-500">Changing this requires re-indexing large documents in the Knowledge Base tab.</p>
        </div>
      </Card>
      <div className="flex gap-3">
        <PrimaryButton data-testid="save-model-button" onClick={save} disabled={!!busy}>{busy === "save" && <Loader2 size={16} className="animate-spin" />} Save</PrimaryButton>
        <button data-testid="test-connection-button" onClick={test} disabled={!!busy} className="border border-slate-300 bg-white hover:bg-slate-50 px-5 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-50">
          {busy === "test" ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} Test connection
        </button>
      </div>
    </div>
  );
};
